import { useTagManager } from '@hooks'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'

import logoSrc from '../../../assets/signets/instagram.svg'
import type { ConnectBarProps } from '../../../components/ConnectBar/ConnectBar'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useOAuthDialog } from '../../../hooks/useOAuthDialog'
import { useCreateInstagramIntegrationMutation } from '../queries/useCreateInstagramIntegrationMutation'
import { useInstagramIntegrationQuery } from '../queries/useInstagramIntegrationQuery'
import { useRemoveInstagramIntegrationMutation } from '../queries/useRemoveInstagramIntegrationMutation'

export const useInstagramFeed = () => {
  const { currentShopQuery } = useCurrentShopQuery()
  const { createInstagramIntegrationMutation } = useCreateInstagramIntegrationMutation()
  const { removeInstagramIntegrationMutation } = useRemoveInstagramIntegrationMutation()
  const { instagramFeedIntegrationQuery } = useInstagramIntegrationQuery(
    [currentShopQuery.data?.id!],
    {
      enabled: !!currentShopQuery.data?.id,
      onError: undefined,
    },
  )
  const tagManager = useTagManager()
  const appConnectEvent = useAppConnectEvent('instagram_feed')

  const { openOAuthDialog } = useOAuthDialog({
    onComplete: () => {
      instagramFeedIntegrationQuery.refetch()
      tagManager.trigger('app_connected', { app_name: 'instagram_feed' })
    },
  })

  const getStatus = (): 'NOT_CONNECTED' | 'CONNECTED' | undefined => {
    if (instagramFeedIntegrationQuery.data?.consumerToken.status === 'NotConnected') {
      return 'NOT_CONNECTED'
    }

    if (instagramFeedIntegrationQuery.data?.consumerToken.status === 'Connected') {
      return 'CONNECTED'
    }
  }

  const status = getStatus()

  const getConnectBar = (): ConnectBarProps => {
    const connectBarBase = {
      logoSrc,
      description: 'Connect to your Instagram account',
      disabled: true,
    }

    if (status === 'CONNECTED') {
      const onDisconnect = async () => {
        await removeInstagramIntegrationMutation.mutateAsync([currentShopQuery.data?.id!])
        await instagramFeedIntegrationQuery.refetch()
      }

      return {
        ...connectBarBase,
        disabled: false,
        onDisconnect,
      }
    }

    if (status === 'NOT_CONNECTED') {
      const onConnect = async () => {
        appConnectEvent.trigger()

        const integration = await createInstagramIntegrationMutation.mutateAsync([
          currentShopQuery.data?.id!,
          { flow: 'postMessage' },
        ])

        openOAuthDialog(integration.redirectTo)
      }

      return {
        ...connectBarBase,
        disabled: false,
        onConnect,
      }
    }

    return connectBarBase
  }

  return {
    instagram: {
      connectBar: getConnectBar(),
      status,
    },
  }
}
